import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Building2, Users, CalendarClock, Download, FileText, Loader2, Crown, AlertTriangle } from "lucide-react";
import { money, vatInfo, annualSaving } from "@/lib/pricing";

const STATUS = {
  active: { it: "Attivo", en: "Active", color: "#16a34a", icon: CheckCircle2 },
  trial: { it: "In prova", en: "Trial", color: "#2563eb", icon: Crown },
  pending_payment: { it: "In attesa di pagamento", en: "Pending payment", color: "#b45309", icon: AlertTriangle },
  payment_failed: { it: "Pagamento fallito", en: "Payment failed", color: "#ef4444", icon: AlertTriangle },
  expired: { it: "Scaduto", en: "Expired", color: "#64748b", icon: AlertTriangle },
  none: { it: "Nessun abbonamento", en: "No subscription", color: "#64748b", icon: AlertTriangle },
};

function StatusBadge({ status, lang }) {
  const s = STATUS[status] || STATUS.none;
  const Icon = s.icon;
  return (
    <span data-testid="subscription-status-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
      style={{ background: `${s.color}18`, color: s.color }}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.5} /> {lang === "en" ? s.en : s.it}
    </span>
  );
}

export default function Subscription() {
  const { lang, refreshUser } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pl, ms, hi, inv] = await Promise.all([
        api.get("/plans"), api.get("/payments/my-subscription"),
        api.get("/payments/history"), api.get("/payments/invoices"),
      ]);
      setPlans(pl.data.plans || []);
      setSub(ms.data || {});
      setPayments(hi.data.payments || []);
      setInvoices(inv.data.invoices || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (planKey) => {
    setBusy(planKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_key: planKey, interval, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); setBusy(null); }
  };

  const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };
  const currentPlan = sub?.plan;
  const needsPayment = ["pending_payment", "payment_failed", "expired", "none"].includes(sub?.status);

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="subscription-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold">{L("Abbonamento", "Subscription")}</h1>
            <p className="text-muted-foreground text-sm mt-1">{L("Gestisci il tuo piano, i pagamenti e le fatture.", "Manage your plan, payments and invoices.")}</p>
          </div>
          {sub && <StatusBadge status={sub.status || "none"} lang={lang} />}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {L("Caricamento…", "Loading…")}</div>
        ) : (
          <>
            {needsPayment && (
              <div className="rounded-lg border p-4 mb-8 flex items-start gap-3" style={{ borderColor: "#b45309", background: "#b4530910" }} data-testid="subscription-alert">
                <AlertTriangle className="w-5 h-5 mt-0.5" style={{ color: "#b45309" }} />
                <div className="text-sm">
                  {sub?.status === "payment_failed"
                    ? L("Il tuo ultimo pagamento non è andato a buon fine. Scegli un piano qui sotto per riattivare l'account.", "Your last payment failed. Choose a plan below to reactivate your account.")
                    : L("Scegli un piano e completa il pagamento per attivare l'accesso completo a Revenue AI.", "Choose a plan and complete payment to unlock full access to Revenue AI.")}
                </div>
              </div>
            )}

            {/* Current plan summary */}
            {currentPlan && (
              <div className="rounded-xl border border-border p-6 mb-8 grid sm:grid-cols-4 gap-4" data-testid="current-plan-summary">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{L("Piano attuale", "Current plan")}</div>
                  <div className="text-lg font-bold flex items-center gap-2"><Crown className="w-4 h-4 text-primary" /> {sub.plan_name || currentPlan}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {L("Prossimo rinnovo", "Next renewal")}</div>
                  <div className="font-data">{fmtDate(sub.current_period_end || sub.trial_ends_at)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> {L("Strutture incluse", "Structures included")}</div>
                  <div className="font-data">{sub.max_properties ?? "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><Users className="w-3 h-3" /> {L("Collaboratori", "Collaborators")}</div>
                  <div className="font-data">{sub.max_collaborators ?? "—"}</div>
                </div>
              </div>
            )}

            {/* Interval toggle */}
            <div className="flex items-center gap-2 mb-6">
              <div className="inline-flex rounded-lg border border-border p-1" data-testid="interval-toggle">
                <button data-testid="interval-month" onClick={() => setInterval("month")}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${interval === "month" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{L("Mensile", "Monthly")}</button>
                <button data-testid="interval-year" onClick={() => setInterval("year")}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${interval === "year" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{L("Annuale", "Annual")}</button>
              </div>
              {interval === "year" && annualSaving(plans[0])
                ? <span className="text-xs text-green-600 font-medium">{L(`Risparmi ${annualSaving(plans[0]).perMonth.toFixed(2).replace(".", ",")}€/mese`, `Save ${annualSaving(plans[0]).perMonth.toFixed(2)}€/mo`)}</span>
                : null}
            </div>

            {/* Plans grid */}
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-5 mb-12 max-w-xl" data-testid="plans-grid">
              {plans.map((pl) => {
                const price = interval === "month" ? pl.price_monthly : pl.price_annual;
                const isFree = pl.key === "free";
                const isCurrent = currentPlan === pl.key && sub?.status === "active";
                const vat = vatInfo(pl, price, lang);
                const savingY = annualSaving(pl);
                return (
                  <div key={pl.id} className={`rounded-xl border p-6 flex flex-col ${isCurrent ? "border-primary ring-2 ring-primary/20" : "border-border"}`} data-testid={`plan-card-${pl.key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-xl font-bold">{pl.name}</h3>
                      {isCurrent && <span className="text-[10px] uppercase font-bold text-primary">{L("Attuale", "Current")}</span>}
                    </div>
                    <div className="mb-1">
                      <span className="text-3xl font-bold font-data">{money(price, pl.currency)}</span>
                      <span className="text-sm text-muted-foreground">/{interval === "month" ? L("mese", "mo") : L("anno", "yr")}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4" data-testid={`plan-vat-${pl.key}`}>
                      {vat.mode === "plus" ? `${vat.label} · ${L("totale", "total")} ${money(vat.total, pl.currency)}` : vat.label}
                      {interval === "year" && savingY ? ` · ${money(savingY.monthlyEquivalent, pl.currency)}/${L("mese", "mo")}` : ""}
                    </p>
                    <ul className="text-sm space-y-2 mb-6 flex-1">
                      <li className="flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" /> {pl.max_properties ?? "—"} {L("strutture", "structures")}</li>
                      <li className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /> {pl.max_collaborators ?? "—"} {L("collaboratori", "collaborators")}</li>
                    </ul>
                    {isFree ? (
                      <Button variant="outline" size="sm" disabled className="w-full" data-testid={`plan-cta-${pl.key}`}>{L("Gratuito", "Free")}</Button>
                    ) : (
                      <Button size="sm" className="w-full" disabled={isCurrent || busy === pl.key}
                        onClick={() => subscribe(pl.key)} data-testid={`plan-cta-${pl.key}`}>
                        {busy === pl.key ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? L("Piano attivo", "Active plan")
                          : currentPlan && !needsPayment ? L("Cambia piano", "Change plan") : L("Abbonati", "Subscribe")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Invoices */}
            <div className="mb-10">
              <h2 className="font-serif text-xl font-bold mb-3 flex items-center gap-2"><FileText className="w-5 h-5" /> {L("Fatture", "Invoices")}</h2>
              {invoices.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="no-invoices">{L("Nessuna fattura disponibile.", "No invoices yet.")}</p>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden" data-testid="invoices-table">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left px-4 py-2">{L("Numero", "Number")}</th>
                      <th className="text-left px-4 py-2">{L("Data", "Date")}</th>
                      <th className="text-left px-4 py-2">{L("Importo", "Amount")}</th>
                      <th className="text-left px-4 py-2">{L("Stato", "Status")}</th>
                      <th className="text-right px-4 py-2">{L("Scarica", "Download")}</th>
                    </tr></thead>
                    <tbody>
                      {invoices.map((iv) => (
                        <tr key={iv.id} className="border-t border-border" data-testid={`invoice-row-${iv.id}`}>
                          <td className="px-4 py-2 font-data">{iv.number || iv.id.slice(-8)}</td>
                          <td className="px-4 py-2">{fmtDate(iv.created)}</td>
                          <td className="px-4 py-2 font-data">€{iv.amount_paid?.toFixed(2)}</td>
                          <td className="px-4 py-2 capitalize">{iv.status}</td>
                          <td className="px-4 py-2 text-right">
                            {iv.invoice_pdf && <a href={iv.invoice_pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`invoice-download-${iv.id}`}><Download className="w-4 h-4" /> PDF</a>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payment history */}
            <div>
              <h2 className="font-serif text-xl font-bold mb-3">{L("Storico pagamenti", "Payment history")}</h2>
              {payments.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="no-payments">{L("Nessun pagamento registrato.", "No payments recorded.")}</p>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden" data-testid="payments-table">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50"><tr>
                      <th className="text-left px-4 py-2">{L("Data", "Date")}</th>
                      <th className="text-left px-4 py-2">{L("Piano", "Plan")}</th>
                      <th className="text-left px-4 py-2">{L("Periodo", "Interval")}</th>
                      <th className="text-left px-4 py-2">{L("Importo", "Amount")}</th>
                      <th className="text-left px-4 py-2">{L("Stato", "Status")}</th>
                    </tr></thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id} className="border-t border-border">
                          <td className="px-4 py-2">{fmtDate(p.created_at)}</td>
                          <td className="px-4 py-2 capitalize">{p.plan_key}</td>
                          <td className="px-4 py-2">{p.interval === "month" ? L("Mensile", "Monthly") : L("Annuale", "Annual")}</td>
                          <td className="px-4 py-2 font-data">€{Number(p.amount || 0).toFixed(2)}</td>
                          <td className="px-4 py-2">
                            <span className="capitalize" style={{ color: p.payment_status === "paid" ? "#16a34a" : p.payment_status === "failed" ? "#ef4444" : "#b45309" }}>{p.payment_status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
